import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { fetchProjects, fetchSettings } from "../lib/api";

const PAGE_SIZE = 9;

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("All");
    const [page, setPage] = useState(1);

    useEffect(() => {
        fetchProjects().then(setProjects).catch(() => {});
        fetchSettings()
            .then((s) => setCategories(s?.project_categories || s?.categories || []))
            .catch(() => {});
    }, []);

    // Helper: normalise a project's category set into an array (multi first, else primary/legacy).
    const projectCats = (p) => {
        if (p.categories && p.categories.length > 0) return p.categories;
        if (p.primary_category) return [p.primary_category];
        return [];
    };

    // Only show category tabs for categories that actually contain projects.
    const visibleCategories = useMemo(() => {
        const used = new Set();
        projects.forEach((p) => projectCats(p).forEach((c) => used.add(c)));
        return ["All", ...categories.filter((c) => used.has(c))];
    }, [projects, categories]);

    const filtered = useMemo(() => {
        if (activeCat === "All") return projects;
        return projects.filter((p) => projectCats(p).includes(activeCat));
    }, [projects, activeCat]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    // Reset to page 1 when filter changes.
    useEffect(() => { setPage(1); }, [activeCat]);

    return (
        <main data-testid="page-projects" className="pt-32 md:pt-40">
            <Seo
                path="/projects"
                title="Projects — Hotels, Resorts, Restaurants & Residences"
                description="A working archive of hospitality interiors by ASC Interior Co., Ltd. — hotels, resorts, restaurants, serviced residences and private homes, delivered from Bangkok since 2003."
            />
            <section className="px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
                <Reveal>
                    <p className="asc-overline">Index 2003 — 2025</p>
                </Reveal>
                <Reveal delay={100}>
                    <h1 className="serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight font-light">
                        A working <span className="italic">archive</span> of
                        <br />
                        hospitality projects.
                    </h1>
                </Reveal>
                <Reveal delay={180}>
                    <p className="mt-10 max-w-2xl text-lg text-[#5C5855] leading-relaxed">
                        Hotels, resorts, restaurants, serviced residences and private
                        homes, delivered from our Bangkok studio since 2003.
                    </p>
                </Reveal>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-32">
                {visibleCategories.length > 1 && (
                    <div
                        data-testid="project-filters"
                        className="border-t border-[#E5E1DB] pt-8 mb-12 flex flex-wrap gap-x-6 gap-y-3"
                    >
                        {visibleCategories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setActiveCat(c)}
                                data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                                className={`pb-1 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                                    activeCat === c
                                        ? "text-[#1A1A1A] border-b border-[#1A1A1A]"
                                        : "text-[#8C7A6B] hover:text-[#1A1A1A]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                )}

                {filtered.length === 0 ? (
                    <p className="text-[#5C5855]" data-testid="projects-empty">No projects match this filter.</p>
                ) : (
                    <div
                        data-testid="projects-grid"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 items-start"
                    >
                        {paged.map((p, i) => (
                            <Reveal key={p.slug} delay={(i % 3) * 70}>
                                <Link
                                    to={`/projects/${p.slug}`}
                                    data-testid={`project-card-${p.slug}`}
                                    className="block group h-full"
                                >
                                    <div className="asc-img-zoom aspect-[4/5] mb-5">
                                        <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="asc-overline">{p.category} · {p.location}</p>
                                            <h3 className="serif text-2xl tracking-tight mt-2">{p.title}</h3>
                                            {p.summary && (
                                                <p className="text-[#5C5855] mt-3 leading-relaxed text-sm line-clamp-3">
                                                    {p.summary}
                                                </p>
                                            )}
                                        </div>
                                        <ArrowUpRight size={18} className="shrink-0 text-[#8C7A6B] mt-2 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                )}

                {totalPages > 1 && (
                    <Pagination
                        page={safePage}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                )}
            </section>
        </main>
    );
}

function Pagination({ page, totalPages, onChange }) {
    // Compact page list: 1 … prev current next … last
    const pages = useMemo(() => {
        const set = new Set([1, totalPages, page, page - 1, page + 1]);
        return Array.from(set)
            .filter((n) => n >= 1 && n <= totalPages)
            .sort((a, b) => a - b);
    }, [page, totalPages]);

    return (
        <nav
            data-testid="projects-pagination"
            className="mt-16 flex items-center justify-center gap-2 border-t border-[#E5E1DB] pt-10"
        >
            <button
                data-testid="page-prev"
                disabled={page <= 1}
                onClick={() => onChange(page - 1)}
                className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed"
            >
                ← Prev
            </button>
            {pages.map((n, idx) => {
                const prev = pages[idx - 1];
                const gap = prev && n - prev > 1;
                return (
                    <span key={n} className="flex items-center gap-2">
                        {gap && <span className="text-[#8C7A6B]">…</span>}
                        <button
                            data-testid={`page-${n}`}
                            onClick={() => onChange(n)}
                            className={`w-9 h-9 text-sm tracking-tight transition-colors ${
                                page === n
                                    ? "bg-[var(--asc-yellow)] text-[#1A1A1A]"
                                    : "text-[#5C5855] hover:text-[#1A1A1A]"
                            }`}
                        >
                            {n}
                        </button>
                    </span>
                );
            })}
            <button
                data-testid="page-next"
                disabled={page >= totalPages}
                onClick={() => onChange(page + 1)}
                className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed"
            >
                Next →
            </button>
        </nav>
    );
}
